import React from 'react';
import { theme } from 'antd';
import { Button, Flex, Progress, Table, Typography } from 'zui/Antd';
import { CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { StatCard } from './index';

const { Text } = Typography;

const StepFour = ({ provisioningProgress, provisioningSummary, provisioningLogs, stats, failedCards }) => {
	const {
		token: { marginLG, marginXS, paddingSM, colorSuccess, colorTextSecondary, colorError, colorPrimary, fontSizeSM }
	} = theme.useToken();
	const isComplete = provisioningSummary !== null;

	const handleDownloadErrorReport = () => {
		if (!failedCards?.length) return;
		const headers = ['cardName', 'cardSerialNumber', 'reason'];
		const rows = failedCards.map((c) => [
			c.cardName || c.name || '',
			c.cardSerialNumber || c.csn || c.uniqueIdentifier || '',
			c.reason || c.error || ''
		]);
		const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
		const a = document.createElement('a');
		a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
		a.download = 'failed_cards_report.csv';
		a.click();
	};

	const logColumns = [
		{
			title: 'Status',
			dataIndex: 'success',
			key: 'status',
			width: 70,
			align: 'center',
			render: (success) =>
				success ? (
					<CheckCircleOutlined style={{ color: colorSuccess, fontSize: 14 }} />
				) : (
					<CloseCircleOutlined style={{ color: colorError, fontSize: 14 }} />
				)
		},
		{
			title: 'Card name',
			dataIndex: 'cardName',
			key: 'cardName',
			width: 140,
			ellipsis: true,
			render: (text) => <Text style={{ fontSize: fontSizeSM }}>{text}</Text>
		},
		{
			title: 'Card serial number',
			dataIndex: 'cardserialnumber',
			key: 'cardserialnumber',
			width: 170,
			render: (text) =>
				text ? (
					<Text code style={{ fontSize: 11 }}>
						{text}
					</Text>
				) : (
					'—'
				)
		},
		{
			title: 'Details',
			key: 'details',
			ellipsis: true,
			render: (_, record) => (
				<Text style={{ fontSize: fontSizeSM }}>
					{record.success ? (
						<Text type='secondary'>Created successfully</Text>
					) : (
						<Text type='danger'>{record.error}</Text>
					)}
				</Text>
			)
		},
		{
			title: 'Time',
			dataIndex: 'timestamp',
			key: 'time',
			width: 90,
			align: 'right',
			render: (text) => (
				<Text type='secondary' style={{ fontSize: 11 }}>
					{text}
				</Text>
			)
		}
	];

	return (
		<Flex vertical gap={marginLG}>
			<Progress
				percent={provisioningProgress}
				status={!isComplete ? 'active' : 'normal'}
				strokeColor={colorPrimary}
			/>

			{isComplete && (
				<>
					<Flex gap={paddingSM}>
						<StatCard
							value={provisioningSummary.succeeded}
							label='Succeeded'
							color={colorTextSecondary}
							bgColor='transparent'
						/>
						<StatCard
							value={stats.warn}
							label='Unassigned'
							color={colorTextSecondary}
							bgColor='transparent'
						/>
						<StatCard
							value={provisioningSummary.failed}
							label='Failed'
							color={colorTextSecondary}
							bgColor='transparent'
						/>
					</Flex>
					{provisioningSummary.failed > 0 && (
						<Button icon={<DownloadOutlined />} onClick={handleDownloadErrorReport}>
							Download error report
						</Button>
					)}
				</>
			)}

			<Flex vertical gap={marginXS}>
				<Text strong style={{ fontSize: fontSizeSM }}>
					Provisioning log
				</Text>

				<Table
					columns={logColumns}
					dataSource={provisioningLogs}
					rowKey='id'
					size='small'
					pagination={false}
					loading={!isComplete && provisioningLogs.length === 0}
					scroll={{ y: 220 }}
					locale={{ emptyText: 'Waiting for progress...' }}
				/>
			</Flex>
		</Flex>
	);
};

export default StepFour;
